import { Badge, Button, EmptyState, Spinner, useToast } from '@/shared/ui';
import { SettingsFrame } from '@/modules/settings/components/SettingsFrame';
import patterns from '@/shared/ui/styles/patterns.module.css';
import type { Connections } from '../api/verificationApi';
import { useConnections, useRefreshCodeMap, useSyncOpenDental } from '../hooks/useConnections';
import { StediDiagnosticsCard } from '../components/StediDiagnosticsCard';
import { IvSubnav } from '../components/IvSubnav';
import { formatTimestamp, getErrorMessage, pluralize } from '../utils/format';
import styles from './ConnectionsPage.module.css';

const PROVIDER_SOURCE_LABEL: Record<
  'open_dental' | 'location_credentials' | 'staff_credential' | 'env_fallback',
  string
> = {
  open_dental: 'from Open Dental',
  location_credentials: 'from Practice Credentials (group NPI)',
  staff_credential: 'from Practice Credentials (provider NPI)',
  env_fallback: 'environment default',
};

function StediBadge({ stedi }: { stedi: Connections['stedi'] }) {
  if (!stedi.configured) return <Badge variant="error">Not connected</Badge>;
  if (stedi.testMode) return <Badge variant="warning">Connected · Test mode</Badge>;
  return <Badge variant="success">Connected · Production</Badge>;
}

export function ConnectionsPage({
  embedded = false,
  level = 2,
}: { embedded?: boolean; level?: 2 | 3 } = {}) {
  const toast = useToast();
  const { connections, isLoading, isError, error, refetch } = useConnections();
  const refreshCodeMap = useRefreshCodeMap();
  const sync = useSyncOpenDental();

  function handleRefresh() {
    refreshCodeMap.mutate(undefined, {
      onSuccess: (r) =>
        toast.success(
          `Category map refreshed · ${Object.keys(r.covCat).length} of ${r.covCatRows} coverage categories · ${r.codeGroupCount} code groups`,
        ),
      onError: (err) => toast.error(getErrorMessage(err, "Couldn't refresh the category map")),
    });
  }

  function handleSync() {
    sync.mutate(undefined, {
      onSuccess: (r) =>
        toast.success(
          `Synced ${r.fetched} appointments · ${r.created} new · ${r.updated} updated · ${r.checksQueued} checks started`,
        ),
      onError: (err) => toast.error(getErrorMessage(err, 'Sync failed')),
    });
  }

  const c = connections;
  const covCatMapped = c ? Object.keys(c.codeMap.covCat).length : 0;

  return (
    <SettingsFrame
      embedded={embedded}
      level={level}
      id="settings-iv-connections"
      pageClassName={patterns.page}
      title="Connections"
      subtitle="Insurance Verification needs an eligibility clearinghouse and a link to your practice software."
    >
      {!embedded && <IvSubnav />}

      {isLoading && !c && (
        <div className={styles.loading}>
          <Spinner />
        </div>
      )}

      {isError && (
        <div className={patterns.panel}>
          <EmptyState
            compact
            title="Couldn't load connection status"
            description={getErrorMessage(error, 'Try again in a moment.')}
            action={{ label: 'Retry', onClick: () => refetch() }}
          />
        </div>
      )}

      {c && (
        <>
          <div className={styles.conn}>
            <div className={`${styles.logo} ${styles.logoStedi}`} aria-hidden>
              St
            </div>
            <div className={styles.info}>
              <div className={styles.connName}>Stedi — eligibility clearinghouse</div>
              <div className={styles.connDesc}>
                Electronic 270/271 eligibility checks. Test mode uses mock payers so you can build
                without real patient data.
              </div>
              <div className={styles.connMeta}>
                {c.stedi.transport === 'fixture' && (
                  <span>Replaying recorded responses — no network</span>
                )}
                {c.stedi.configured && c.stedi.baseUrl && <span>{c.stedi.baseUrl}</span>}
                {!c.stedi.configured && (
                  <span>Add the Stedi API key to the backend environment to enable checks.</span>
                )}
                {c.providerIdentity?.configured ? (
                  c.providerIdentity.isFallback ? (
                    <span className={styles.warn}>
                      Provider NPI is the environment default (…{c.providerIdentity.npiLast4}) — add
                      this practice&apos;s own NPI under Practice credentials before going live.
                    </span>
                  ) : (
                    <span>
                      Provider NPI …{c.providerIdentity.npiLast4} ·{' '}
                      {c.providerIdentity.organizationName} ·{' '}
                      {PROVIDER_SOURCE_LABEL[c.providerIdentity.source]}
                    </span>
                  )
                ) : c.providerIdentity ? (
                  <span className={styles.warn}>{c.providerIdentity.message}</span>
                ) : c.provider.npiConfigured ? (
                  <span>
                    Provider NPI fallback on file
                    {c.provider.organizationName ? ` · ${c.provider.organizationName}` : ''}
                  </span>
                ) : (
                  <span className={styles.warn}>
                    No provider NPI configured — checks will fail until Practice Credentials are
                    set.
                  </span>
                )}
              </div>
            </div>
            <StediBadge stedi={c.stedi} />
          </div>

          <div className={styles.conn}>
            <div className={`${styles.logo} ${styles.logoOd}`} aria-hidden>
              OD
            </div>
            <div className={styles.info}>
              <div className={styles.connName}>Open Dental — practice software</div>
              <div className={styles.connDesc}>
                Reads tomorrow's schedule and writes verified benefits back into the benefit table.
              </div>
              <div className={styles.connMeta}>
                <span>
                  Write mode:{' '}
                  <strong>{c.openDental.writeMode === 'dry_run' ? 'dry run' : 'live'}</strong>
                  {c.openDental.writeMode === 'dry_run' &&
                    ' — approvals are logged, nothing is written'}
                </span>
                <span>
                  Coverage categories mapped: {covCatMapped} of {c.codeMap.covCatRows} covcats ·
                  code groups: {c.codeMap.codeGroupCount} ·{' '}
                  {c.codeMap.fetchedAt
                    ? `fetched ${formatTimestamp(c.codeMap.fetchedAt)}`
                    : 'not fetched yet'}
                </span>
                {!c.openDental.configured && (
                  <span>Add the Open Dental Developer + Customer API keys to connect.</span>
                )}
              </div>
              {c.openDental.configured && (
                <div className={styles.connActions}>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={handleRefresh}
                    isLoading={refreshCodeMap.isPending}
                  >
                    Refresh category map
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={handleSync}
                    isLoading={sync.isPending}
                  >
                    Sync upcoming appointments
                  </Button>
                </div>
              )}
            </div>
            {c.openDental.configured ? (
              <Badge variant="success">Connected</Badge>
            ) : (
              <Badge variant="error">Not connected</Badge>
            )}
          </div>

          <p className={styles.mappings}>
            Carrier → payer matches saved:{' '}
            <strong>{pluralize(c.carrierMappings, 'match', 'matches')}</strong>. Matches are learned
            during schedule imports and reused automatically.
          </p>

          <StediDiagnosticsCard />

          <div className={styles.note}>
            <strong>Setup order.</strong> 1) Create a free Stedi account and use the developer
            portal in test mode with mock requests — no real PHI. 2) Connect Open Dental (Developer
            + Customer API key). 3) Map this practice's coverage categories and code groups once. 4)
            Switch Stedi to production and enroll the few payers (like CMS) that require it.
            Everything before step 4 runs on mock data.
          </div>
        </>
      )}
    </SettingsFrame>
  );
}
